/* eslint-disable no-useless-escape */
import { DateTime } from 'luxon';
import SirenParse from 'siren-parser';
import { Measurements, getBinLevelMeasurementOverrides, sirenActions, entityStore } from '~/utility/BinSentry-ui-utility';
import { LitEntity, SirenActionMixin, html } from '../../utility/lit-entity';
import { BnModalMixin } from '../../common/bn-modal-mixin';
import { getRefillRange } from './bin-readings';
import { formStyles } from '../../styles/form-styles';
import i18n from '../../../../i18n';
import '../../common/bn-action';
import '../../common/bn-flash';
import '../../common/bn-svg-icon';

const COLUMN_NAMES = [
  { key: 'feed_type', name: () => i18n.t('exportBinModal.feedType') },
  { key: 'org_path', name: () => i18n.t('exportBinModal.organizationPath') },
  { key: 'capacity', name: () => i18n.t('exportBinModal.binCapacity') },
  { key: 'tags', name: () => i18n.t('exportBinModal.tags') },
];

class ExportBinModal extends BnModalMixin(SirenActionMixin(LitEntity)) {
  constructor() {
    super();
    this.id = 'export-bin-modal';
    // Run the initially set start/end dates through validation
    this._validateCurrentDates();
    this.localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.includedColumns = [];
  }

  static get properties() {
    return {
      actionHref: { type: String, attribute: false },
      levelsEntity: { type: Object, attribute: false },
      millName: { type: String, attribute: false },
      customerName: { type: String, attribute: false },
      siteName: { type: String, attribute: false },
      barnName: { type: String, attribute: false },
      binPath: { type: String, attribute: false },
      binName: { type: String, attribute: false },
      binTags: { type: String, attribute: false },
      binTagsHeaders: { type: String, attribute: false },
      binVolume: { type: String, attribute: false },
      orderFeedType: { type: String, attribute: false },
      loading: { type: Boolean, attribute: false },
      fetching: { type: Boolean, attribute: false },
      startValid: { type: Boolean, attribute: false },
      endValid: { type: Boolean, attribute: false },
      start: { type: String, reflect: true },
      end: { type: String, reflect: true },
      includedColumns: { type: Array, attribute: false },
      localTimezone: { type: String, attribute: false },
      selectedTimezone: { type: String, attribute: false },
      binLevelMeasurement: { type: Object, attribute: false },
    };
  }

  renderContents() {
    return html`
      <style>
        ${formStyles}
        #export-bin-modal {
          --modal-min-width: 50%;
        }
        #export-bin-modal .modal-body {
          text-align: center;
        }
        .content-container {
          max-width: 800px;
          margin: 0 auto 1em auto;
        }
        .content-container * {
          text-align: center;
        }
        h3 {
          margin: 0;
        }
        p {
          margin: 1em 0 0 0;
        }
        .export-dates-container {
          display: flex;
          align-items: center;
          justify-content: space-evenly;
        }
        .export-dates-container input {
          max-width: 300px;
        }
        h4 {
          font-size: 1.125em;
        }
        .row {
          margin-top: 1em;
          margin-bottom: .25em;
        }
      </style>

      <h3 slot="title">${i18n.t('exportBinModal.title')}</h3>

      <div class="content-container">
        <bn-flash id="status"></bn-flash>

        <div class="row">
          <h4>${i18n.t('exportBinModal.exportCsvFileFromBin')}</h4>
          <bin-path-name .href="${this.href}"></bin-path-name>
        </div>

        <div class="row">
          <h4>${i18n.t('exportBinModal.betweenTheDatesOf')}</h4>
          <div class="export-dates-container">
            <input
              type="date"
              value="${new Date(this.start).toLocaleDateString('en-CA')}"
              max="${new Date().toLocaleDateString('en-CA')}"
              @change="${(e) => this._dateChanged(e.target.value, 'start', 'startValid')}"
            />
            ${i18n.t('exportBinModal.dateSeparator')}
            <input
              type="date"
              value="${new Date(this.end).toLocaleDateString('en-CA')}"
              max="${new Date().toLocaleDateString('en-CA')}"
              @change="${(e) => this._dateChanged(e.target.value, 'end', 'endValid')}"
            />
          </div>
        </div>
        <div class="row">
          <h4>${i18n.t('exportBinModal.timezone')}</h4>
          <select @input="${(e) => this._onSelectTimezone(e.target.value)}">
            <option value="local">${i18n.t('exportBinModal.localTime', { timezone: this.localTimezone })}</option>
            <option value="utc">${i18n.t('exportBinModal.utc')}</option>
          </select>
        </div>
        <div class="row">
          <h4>${i18n.t('exportBinModal.additionalColumns')}</h4>
          <div class="check-boxes">
            ${this._renderCheckBoxes()}
          </div>
        </div>
        <div class="row">
          <button class="button button--contained" @click="${this._onClickExport}" ?disabled="${!this._canExportLevels()}">
            <bn-svg-icon name="FileDocumentArrowRight"></bn-svg-icon> ${i18n.t('exportBinModal.export')}
          </button>
        </div>
      </div>
    `;
  }

  _renderCheckBoxes() {
    const canViewOrders = !!this.getHref('https://api.binsentry.com/rel/most-relevant-order');
    const filteredColumnNames = COLUMN_NAMES.filter((columnName) => {
      if (columnName.key === 'feed_type') {
        return canViewOrders;
      }
      return true;
    });
    return filteredColumnNames.map((columnName) => {
      const checked = (this.includedColumns || []).indexOf(columnName.key) > -1;
      return html`
        <label class="${columnName.key} ${checked ? 'checked' : ''}">
          <input
            type="checkbox"
            ?checked=${checked}
            @input="${() => this._onCheckboxInput(columnName.key)}"
          >
          <span title="label">${typeof columnName.name === 'function' ? columnName.name() : columnName.name}</span>
        </label>
      `;
    });
  }

  _onCheckboxInput(columnName) {
    const index = this.includedColumns.indexOf(columnName);
    const columnsDup = this.includedColumns.map((column) => column);
    if (index === -1) {
      columnsDup.push(columnName);
    } else {
      columnsDup.splice(index, 1);
    }
    this.includedColumns = columnsDup;
  }

  _onSelectTimezone(timezone) {
    this.selectedTimezone = timezone;
  }

  async _entityChanged(entity) {
    super._entityChanged(entity);
    if (!this.entity) {
      return;
    }

    this.fetching = true;
    this.binVolume = this.entity.properties.volumes.fullTotal;

    // Levels
    const levelsEntityIndex = await this.fetchSiren(this.getHref('https://api.binsentry.com/rel/v2/bin-levels'));
    const dateRangeAction = levelsEntityIndex.getActionByName('custom-date-range');
    const dateRangeActionFields = this.getActionFields(dateRangeAction);
    const now = DateTime.utc();
    this.start = now.minus({ week: 1 }).toISO();
    this.end = now.toISO();
    dateRangeActionFields.set('startDate', this.start);
    dateRangeActionFields.set('endDate', this.end);
    const levelsHref = this.parseAction(dateRangeAction, dateRangeActionFields).url.href;

    const { entity: levelsJson } = await entityStore.fetchWait(levelsHref);
    this.levelsEntity = SirenParse(levelsJson);

    // Feed type
    const ordersHref = this.getHref('https://api.binsentry.com/rel/most-relevant-order');
    if (ordersHref) {
      const { entity: ordersJson } = await entityStore.fetchWait(ordersHref);
      const ordersEntity = SirenParse(ordersJson);
      const feed = ordersEntity.getSubEntitiesByClass('feed');
      if (feed.length) {
        const ordersFeedHref = feed[0].href;
        const { entity: ordersFeedEntity } = await entityStore.fetchWait(ordersFeedHref);
        this.orderFeedType = ordersFeedEntity.properties.feedType;
      }
    }

    // Tags
    const tagsHref = this.getHref('https://api.binsentry.com/rel/tags');
    const { entity: tagsJson } = await entityStore.fetchWait(tagsHref);
    const tagsEntity = SirenParse(tagsJson);
    this.binTagHeaders = tagsEntity.entities.map((tag) => `\"${tag.properties.key}\"`).join(', ');
    this.binTags = tagsEntity.entities.map((tag) => `\"${tag.properties.value}\"`).join(', ');

    // Bin path name
    this.binName = this.entity.properties.name;
    const binOwnerHref = this.getHref('https://api.binsentry.com/rel/bin-owner');
    const { entity: binOwnerJson } = await entityStore.fetchWait(binOwnerHref);
    const binOwnerEntity = SirenParse(binOwnerJson);

    const action = binOwnerEntity?.getAction('get-ancestors');
    const fields = action && sirenActions.getActionFields(action);
    if (fields) {
      fields.set('includeInitialOrganization', true);
      fields.set('startOrganizationType', '');
      const endOrgField = action.fields.find((item) => item.name === 'endOrganizationType');
      const accountValue = endOrgField?.value.find((item) => item.title === 'account').value;
      fields.set('endOrganizationType', accountValue);

      const { entity: ancestorsEntity } = await this.submitAction(action, fields);
      this.binPath = ancestorsEntity.properties.pathName;
      const getOrganizationNameByType = (type) => ancestorsEntity.getSubEntitiesByClass('organization').find((organization) => organization.getSubEntityByRel('https://api.binsentry.com/rel/organization-type').properties.name === type).properties.name;
      this.millName = getOrganizationNameByType('account');
      this.customerName = getOrganizationNameByType('customer');
      this.siteName = getOrganizationNameByType('farm');
      this.barnName = getOrganizationNameByType('barn');
    }
    this.fetching = false;

    this.binLevelMeasurement = Measurements.get('binLevel', await getBinLevelMeasurementOverrides(this.entity));
  }

  async _onClickExport() {
    if (!this.levelsEntity) {
      return;
    }

    this.loading = true;
    try {
      const action = this.levelsEntity.getActionByName('custom-date-range');
      const fields = this.getActionFields(action);
      fields.set('startDate', this.start);
      fields.set('endDate', this.end);

      const { entity } = await this.submitAction(action, fields);
      const csvString = this._createCSV(entity.getSubEntitiesByClass('level'));
      const binNameNoWhitespace = this.entity.properties.name.replace(/\s/g, '');
      const filename = `Levels-Export-${binNameNoWhitespace}-${new Date(this.start).toISOString()}-${new Date(this.end).toISOString()}.csv`;
      this._downloadTextAsFile(filename, csvString);
    } catch (err) {
      this._flashError();
      throw err;
    } finally {
      this.loading = false;
    }
  }

  _flashError() {
    const flash = this.renderRoot.querySelector('#status');
    flash.clear();
    flash.critical(html`
      ${i18n.t('exportBinModal.errorExportingBinLevels')}
      <br/>
      <code>${i18n.t('exportBinModal.closeModalAndTryAgain')}</code>
    `);
  }

  _validateCurrentDates() {
    this._dateChanged(this.start || new Date().toISOString(), 'start', 'startValid');
    this._dateChanged(this.end || new Date().toISOString(), 'end', 'endValid');
  }

  update(changedAttributes) {
    if (changedAttributes.has('hide')) {
      this._validateCurrentDates();
    }
    super.update(changedAttributes);
  }

  _createCSV(levelEntities) {
    const volumeConverter = Measurements.get('volume');
    const showPercent = this.binLevelMeasurement.unit === '%';
    const col4Converter = showPercent ? this.binLevelMeasurement : Measurements.get('weight');
    const col4Title = showPercent ? `${i18n.t('exportBinModal.csvPercentFull')} (${col4Converter.unit})` : `${i18n.t('exportBinModal.csvWeight')} (${col4Converter.unit})`;

    let csvString = `${i18n.t('exportBinModal.csvTimezone')},${i18n.t('exportBinModal.csvDate')},${i18n.t('exportBinModal.csvVolume')} (${volumeConverter.unit}),${col4Title},${i18n.t('exportBinModal.csvAdditionalInformation')}`;
    if (this.includedColumns) {
      COLUMN_NAMES.forEach((column) => {
        if (this.includedColumns.includes(column.key)) {
          switch (column.key) {
          case 'capacity':
            csvString += `,${typeof column.name === 'function' ? column.name() : column.name} (${volumeConverter.unit})`;
            break;
          case 'tags':
            csvString += `,${this.binTagHeaders}`;
            break;
          case 'org_path':
            csvString += `,${typeof column.name === 'function' ? column.name() : column.name},${i18n.t('exportBinModal.mill')},${i18n.t('exportBinModal.customer')},${i18n.t('exportBinModal.site')},${i18n.t('exportBinModal.barn')}`;
            break;
          default:
            csvString += `,${typeof column.name === 'function' ? column.name() : column.name}`;
            break;
          }
        }
      });
    }
    csvString += '\n';

    for (let i = 0; i < levelEntities.length; i++) {
      const level = levelEntities[i];
      const props = { ...level.properties, totalVolume: this.binVolume };
      let dateCell = props.publishedAt;
      let timezoneCell = 'UTC';
      if (this.selectedTimezone !== 'utc') {
        dateCell = DateTime.fromISO(props.publishedAt).toLocal().toISO({ includeOffset: false });
        timezoneCell = this.localTimezone;
      }

      csvString += `${timezoneCell},${dateCell},${volumeConverter.convert(props)},${col4Converter.convert(props)},${this._additionalInformationText(levelEntities, i)}`;
      if (this.includedColumns) {
        COLUMN_NAMES.forEach((column) => {
          if (this.includedColumns.includes(column.key)) {
            switch (column.key) {
            case 'feed_type':
              csvString += `,\"${this.orderFeedType || ''}\"`;
              break;
            case 'org_path':
              csvString += `,\"${this.binPath} > ${this.binName}\"`;
              csvString += `,\"${this.millName}\",\"${this.customerName}\",\"${this.siteName}\",\"${this.barnName}\"`;
              break;
            case 'capacity':
              csvString += `,${volumeConverter.convert( { volume: this.binVolume } )}`;
              break;
            case 'tags':
              csvString += `,${this.binTags}`;
              break;
            }
          }
        });
      }
      csvString += '\n';
    }
    return csvString;
  }

  _additionalInformationText(levels, currentLevelIndex) {
    const currentLevel = levels[currentLevelIndex];
    const lastLevel = levels[currentLevelIndex - 1];
    if (currentLevel.properties.label !== 'REFILL' || !lastLevel) {
      return '';
    }

    const { volumes: { total: totalVolume }, bulkDensity } = this.entity.properties;
    const refillRange = getRefillRange({
      levels,
      currentLevelIndex,
      bulkDensity,
      totalVolume,
    });

    const convertedRangeLow = this.binLevelMeasurement.convert({ volume: refillRange.low, bulkDensity, totalVolume });
    const convertedRangeHigh = this.binLevelMeasurement.convert({ volume: refillRange.high, bulkDensity, totalVolume });
    return i18n.t('exportBinModal.refillDetected', { low: convertedRangeLow, high: convertedRangeHigh, unit: this.binLevelMeasurement.unit });
  }

  _dateChanged(newDateValue, dateName, dateValidName) {
    try {
      this[dateName] = new Date(newDateValue).toISOString();
      this[dateValidName] = true;
    } catch (e) {
      this[dateValidName] = false;
    }
  }

  _canExportLevels() {
    return this.endValid && this.startValid && !this.loading && !this.fetching && this.binLevelMeasurement;
  }

  _downloadTextAsFile(filename, text) {
    const ele = document.createElement('a');
    ele.style.display = 'none';
    ele.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
    ele.setAttribute('download', filename);
    ele.click();
  }
}

window.customElements.define('export-bin-modal', ExportBinModal);
